import { useEffect, useRef, useState } from 'react'
import { Heading, Text } from '../../components/index.js'
import { cn } from '../../helpers/index.js'
import type { Media, Theme } from '../../types/props.js'
import { withLayout } from '../../shared/Styles.js'

function LazyMedia({ children, offset = 800 }: { children: React.ReactNode; offset?: number }) {
  const [loaded, setLoaded] = useState(false)
  const ref = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const observer = new IntersectionObserver(([entry]) => entry.isIntersecting && setLoaded(true), {
      rootMargin: `${offset}px`,
    })
    ref.current && observer.observe(ref.current)
    return () => observer.disconnect()
  }, [offset])

  return (
    <div ref={ref} className="size-full">
      {loaded && children}
    </div>
  )
}

function MediaElement({ media, poster, className }: { media?: Media; poster?: Media; className?: string }) {
  if (!media?.url) return null

  const isVideo = media.mimeType?.startsWith('video/')

  if (isVideo) {
    return (
      <video
        src={media.url}
        playsInline
        autoPlay
        muted
        loop
        poster={poster?.url || ''}
        preload="metadata"
        disablePictureInPicture
        disableRemotePlayback
        className={className}
        width={media.width}
        height={media.height}
      />
    )
  }

  return <img src={media.url} alt={media.alt || ''} className={className} width={media.width} height={media.height} />
}

export interface ProductItemData {
  /** 产品标签文本 */
  text?: string
  /** 桌面端媒体（视频或图片） */
  media?: Media
  /** 移动端媒体（视频或图片） */
  mobMedia?: Media
  /** 桌面端封面图（仅用于视频） */
  poster?: Media
  /** 移动端封面图（仅用于视频） */
  mobPoster?: Media
}

/** 标题类型 */
export type TitleType = 'selling-point' | 'primary' | 'secondary'

/** 文本对齐方式 */
export type TextAlign = 'left' | 'center'

/** 图片宽度比例（2张图片时） */
export type TwoImageRatio = '2:3' | '1:1'

/** 图片宽度比例（3张图片时） */
export type ThreeImageRatio = '1:1:1'

export interface ProductCompareProps extends React.HTMLAttributes<HTMLElement> {
  /** 组件数据 */
  data: {
    /** 主标题 */
    title?: string
    /** 标题类型：selling-point（卖点）、primary（一级标题）、secondary（二级标题） */
    titleType?: TitleType
    /** 标题图标（仅当 titleType 为 selling-point 时有效） */
    titleIcon?: Media
    /** 副标题 */
    subtitle?: string
    /** 副标题下方的图片 */
    subtitleImage?: Media
    /** 文本对齐方式：left（左对齐）、center（居中） */
    textAlign?: TextAlign
    /** 产品列表（支持2-3个产品） */
    products?: ProductItemData[]
    /** 2张图片时的宽度比例 */
    twoImageRatio?: TwoImageRatio
    /** 3张图片时的宽度比例 */
    threeImageRatio?: ThreeImageRatio
    /** 主题：light 或 dark */
    theme?: Theme
  }
}

/**
 * 根据产品数量和比例获取布局类名
 */
function getProductLayoutClasses(index: number, totalProducts: number, twoImageRatio?: TwoImageRatio): string {
  if (totalProducts === 2) {
    if (twoImageRatio === '1:1') {
      return 'laptop:flex-[1]'
    }
    // 默认 2:3 比例
    return index === 0 ? 'laptop:flex-[2]' : 'laptop:flex-[3]'
  }

  if (totalProducts === 3) {
    // 1:1:1 比例
    return 'laptop:flex-[1]'
  }

  return 'laptop:flex-[1]'
}

function ProductCompare({ data, className, ...rest }: ProductCompareProps) {
  const {
    title,
    titleType = 'primary',
    titleIcon,
    subtitle,
    subtitleImage,
    textAlign = 'left',
    products = [],
    twoImageRatio = '2:3',
    threeImageRatio = '1:1:1',
    theme = 'light',
  } = data

  // 文本对齐类名
  const alignClasses = textAlign === 'center' ? 'text-center' : 'text-left laptop:text-left'

  // 根据标题类型确定 Heading size 和样式
  const getTitleSize = () => {
    if (titleType === 'secondary') return 3
    return 4
  }

  // 渲染标题
  const renderTitle = () => {
    if (!title) return null

    // 卖点标题（包含图标）
    if (titleType === 'selling-point' && titleIcon) {
      return (
        <div
          className={cn(
            'product-compare-title-wrapper flex items-center gap-[8px]',
            alignClasses === 'text-center' ? 'justify-center' : ''
          )}
        >
          <img
            src={titleIcon.url}
            alt={titleIcon.alt || ''}
            className="product-compare-title-icon laptop:size-[32px] size-[24px]"
          />
          <Heading
            as="h2"
            size={getTitleSize()}
            html={title}
            className="product-compare-title"
            style={{ color: '#00BEFA' }}
          />
        </div>
      )
    }

    // 普通标题
    return <Heading as="h2" size={getTitleSize()} html={title} className={cn('product-compare-title', alignClasses)} />
  }

  return (
    <section
      {...rest}
      className={cn('product-compare text-info-primary', className, {
        'aiui-dark': theme === 'dark',
      })}
    >
      {/* 标题区域 */}
      {renderTitle()}

      {/* 副标题 */}
      {subtitle && (
        <Text
          as="p"
          size={2}
          html={subtitle}
          className={cn(
            'product-compare-subtitle tablet:text-[14px] laptop:text-[14px] desktop:text-[16px] lg-desktop:text-[18px] mt-[4px] text-[14px]',
            alignClasses
          )}
        />
      )}

      {/* 副标题下方图片 */}
      {subtitleImage && (
        <div
          className={cn(
            'product-compare-subtitle-image laptop:mt-[24px] mt-[16px]',
            alignClasses === 'text-center' ? 'flex justify-center' : ''
          )}
        >
          <img
            src={subtitleImage.url}
            alt={subtitleImage.alt || ''}
            className="max-w-full"
            width={subtitleImage.width}
            height={subtitleImage.height}
          />
        </div>
      )}

      {/* 产品对比卡片容器 */}
      <div className="product-compare-container tablet:flex-row tablet:flex-nowrap laptop:mt-[32px] mt-[24px] flex w-full flex-col items-stretch gap-[16px]">
        {products.map((product, index) => {
          const isLastProduct = index === products.length - 1
          const layoutClasses = getProductLayoutClasses(index, products.length, twoImageRatio)

          // 标签样式：最后一个产品用 bg-brand-0，其他用 rgba(0,0,0,0.2)
          const labelBgClass = isLastProduct ? 'bg-brand-0' : 'bg-[rgba(0,0,0,0.2)]'

          return (
            <div
              key={index}
              className={cn('product-compare-item rounded-box relative shrink overflow-hidden', layoutClasses, {
                'order-2 tablet:order-1': index === 0 && products.length === 2,
                'order-1 tablet:order-2': index === 1 && products.length === 2,
              })}
            >
              {/* 产品标签 */}
              {product.text && (
                <div
                  className={cn(
                    'product-compare-label rounded-btn laptop:left-[28px] laptop:top-[28px] laptop:px-[28px] laptop:pb-[14px] laptop:pt-[15px] desktop:left-[32px] desktop:top-[32px] absolute left-[16px] top-[16px] z-10 px-[20px] pb-[10px] pt-[11px] font-bold text-white',
                    labelBgClass
                  )}
                >
                  <Heading as="h6" className="font-bold" size={1}>
                    {product.text}
                  </Heading>
                </div>
              )}

              {/* 产品媒体 */}
              <LazyMedia offset={1500}>
                <MediaElement
                  media={product.media}
                  poster={product.poster}
                  className="product-compare-media laptop:block hidden size-full object-cover"
                />
                <MediaElement
                  media={product.mobMedia ? product.mobMedia : product.media}
                  poster={product.mobPoster ? product.mobPoster : product.poster}
                  className="product-compare-media-mobile laptop:hidden block w-full"
                />
              </LazyMedia>
            </div>
          )
        })}
      </div>
    </section>
  )
}

export default withLayout(ProductCompare)
